import React, { useEffect, useMemo, useState } from 'react';
import { Save, RefreshCw, MessageSquare, PhoneCall, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { api, communicationSettingsApi } from '../../../lib/api';
import EmailSettingsForm from './EmailSettingsForm';
import { smsAbsenderFehler, smsAbsenderVorschlag } from '@/lib/smsAbsender';

type SmsProvider = 'none' | 'sms77';
type CtiProvider = 'none' | 'nfon';

type CommunicationSettingsDto = {
  sms: { provider: SmsProvider; apiKey: string };
  cti: { provider: CtiProvider };
  maintenance?: {
    enabled: boolean;
    emailRedirectAddress: string;
    smsRedirectTo: string;
    emailExemptKinds?: string[];
  };
};

type EmailKind = { id: string; label: string };

type StandortItem = { id: number; name: string };
type StandortCommunication = { smsSender: string; nfonAkz: string; emailFrom: string; emailFromName: string };

export default function CommunicationSettings() {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<CommunicationSettingsDto>({
    sms: { provider: 'none', apiKey: '' },
    cti: { provider: 'none' },
    maintenance: { enabled: false, emailRedirectAddress: '', smsRedirectTo: '', emailExemptKinds: [] },
  });
  const [emailKinds, setEmailKinds] = useState<EmailKind[]>([]);

  const exemptKinds = settings.maintenance?.emailExemptKinds || [];
  const toggleExemptKind = (id: string, on: boolean) => {
    setSettings(prev => {
      const current = prev.maintenance?.emailExemptKinds || [];
      const next = on ? Array.from(new Set([...current, id])) : current.filter(k => k !== id);
      return {
        ...prev,
        maintenance: {
          enabled: !!prev.maintenance?.enabled,
          emailRedirectAddress: prev.maintenance?.emailRedirectAddress || '',
          smsRedirectTo: prev.maintenance?.smsRedirectTo || '',
          emailExemptKinds: next,
        },
      };
    });
  };

  const [standorte, setStandorte] = useState<StandortItem[]>([]);
  const [standortMap, setStandortMap] = useState<Record<number, StandortCommunication>>({});
  const [savingStandortId, setSavingStandortId] = useState<number | null>(null);

  // Test SMS
  const [testStandortId, setTestStandortId] = useState<string>('');
  const [testTo, setTestTo] = useState('');
  const [testText, setTestText] = useState('SIDIGO Test-SMS');

  const effectiveTestStandortId = useMemo(() => {
    const n = Number(testStandortId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [testStandortId]);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, sites, map, kinds] = await Promise.all([
        communicationSettingsApi.get(),
        api.get<StandortItem[]>('/api/standorte'),
        communicationSettingsApi.getStandorteMap(),
        communicationSettingsApi.getEmailKinds().catch(() => [] as EmailKind[]),
      ]);
      setSettings(cfg);
      setEmailKinds(kinds || []);
      setStandorte(sites || []);
      const normalized: Record<number, StandortCommunication> = {};
      Object.entries(map || {}).forEach(([k, v]) => {
        const id = Number(k);
        if (Number.isFinite(id)) {
          normalized[id] = {
            smsSender: (v as any)?.smsSender || '',
            nfonAkz: (v as any)?.nfonAkz || '',
            emailFrom: (v as any)?.emailFrom || '',
            emailFromName: (v as any)?.emailFromName || '',
          };
        }
      });
      setStandortMap(normalized);
      // Default for test: first site
      if (!testStandortId && (sites || []).length > 0) {
        setTestStandortId(String(sites[0].id));
      }
    } catch (e: any) {
      toast.error(e?.message || 'Kommunikations-Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveGlobal = async () => {
    setLoading(true);
    try {
      if (settings.maintenance?.enabled) {
        const mail = (settings.maintenance.emailRedirectAddress || '').trim();
        const sms = (settings.maintenance.smsRedirectTo || '').trim();
        const isEmail = /.+@.+\..+/.test(mail);
        if (!mail || !isEmail) {
          toast.error('Bitte eine gültige Redirect-E-Mail-Adresse für den Wartungsmodus angeben');
          setLoading(false);
          return;
        }
        if (!sms) {
          toast.error('Bitte eine Redirect-Telefonnummer für den Wartungsmodus angeben');
          setLoading(false);
          return;
        }
      }
      const saved = await communicationSettingsApi.update(settings);
      setSettings(saved);
      toast.success('Kommunikations-Einstellungen gespeichert');
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const updateStandortField = (id: number, patch: Partial<StandortCommunication>) => {
    setStandortMap(prev => ({
      ...prev,
      [id]: {
        smsSender: patch.smsSender ?? prev[id]?.smsSender ?? '',
        nfonAkz: patch.nfonAkz ?? prev[id]?.nfonAkz ?? '',
        emailFrom: patch.emailFrom ?? prev[id]?.emailFrom ?? '',
        emailFromName: patch.emailFromName ?? prev[id]?.emailFromName ?? '',
      },
    }));
  };

  const saveStandort = async (id: number) => {
    setSavingStandortId(id);
    try {
      const current = standortMap[id] || { smsSender: '', nfonAkz: '', emailFrom: '', emailFromName: '' };
      const saved = await communicationSettingsApi.updateStandort(id, current);
      setStandortMap(prev => ({ ...prev, [id]: saved }));
      toast.success('Standort gespeichert');
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSavingStandortId(null);
    }
  };

  const sendTestSms = async () => {
    const to = testTo.trim();
    if (!effectiveTestStandortId) {
      toast.error('Bitte einen Standort auswählen');
      return;
    }
    if (!to) {
      toast.error('Bitte eine Zielnummer angeben');
      return;
    }
    setLoading(true);
    try {
      const res = await communicationSettingsApi.testSms({
        standortId: effectiveTestStandortId,
        to,
        text: (testText || '').trim() || 'SIDIGO Test-SMS',
      });
      if ((res as any)?.ok) {
        toast.success('Test-SMS wurde versendet');
      } else {
        toast.error((res as any)?.error || 'Test-SMS fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Test-SMS fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Kommunikation</h1>
        <p className="text-muted-foreground">E-Mail, SMS Provider (SMS77) und Click-to-Dial (NFON) konfigurieren</p>
      </div>

      <div className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">E-Mail</h2>
          <p className="text-sm text-muted-foreground">SMTP-Verbindungsdaten, Absender und Wartungsmodus</p>
        </div>
        <EmailSettingsForm />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Wartungsmodus (zentral)</CardTitle>
          <CardDescription>Gilt gleichzeitig für E-Mail und SMS (inkl. Redirects)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between rounded-lg border p-3 md:col-span-1">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">Wartungsmodus aktivieren</div>
                <div className="text-xs text-muted-foreground">Alle Nachrichten umleiten</div>
              </div>
              <Switch
                checked={!!settings.maintenance?.enabled}
                onCheckedChange={(v) =>
                  setSettings(prev => ({
                    ...prev,
                    maintenance: {
                      enabled: v,
                      emailRedirectAddress: prev.maintenance?.emailRedirectAddress || '',
                      smsRedirectTo: prev.maintenance?.smsRedirectTo || '',
                      emailExemptKinds: prev.maintenance?.emailExemptKinds || [],
                    },
                  }))
                }
              />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Redirect-E-Mail-Adresse</label>
                <Input
                  value={settings.maintenance?.emailRedirectAddress || ''}
                  onChange={(e) =>
                    setSettings(prev => ({
                      ...prev,
                      maintenance: {
                        enabled: !!prev.maintenance?.enabled,
                        emailRedirectAddress: e.target.value,
                        smsRedirectTo: prev.maintenance?.smsRedirectTo || '',
                        emailExemptKinds: prev.maintenance?.emailExemptKinds || [],
                      },
                    }))
                  }
                  placeholder="admin@example.com"
                  disabled={!settings.maintenance?.enabled}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Redirect-Telefonnummer</label>
                <Input
                  value={settings.maintenance?.smsRedirectTo || ''}
                  onChange={(e) =>
                    setSettings(prev => ({
                      ...prev,
                      maintenance: {
                        enabled: !!prev.maintenance?.enabled,
                        emailRedirectAddress: prev.maintenance?.emailRedirectAddress || '',
                        smsRedirectTo: e.target.value,
                        emailExemptKinds: prev.maintenance?.emailExemptKinds || [],
                      },
                    }))
                  }
                  placeholder="+49176123456789"
                  disabled={!settings.maintenance?.enabled}
                />
              </div>
            </div>
          </div>
          {emailKinds.length > 0 ? (
            <div className="rounded-lg border p-3">
              <div className="mb-1 text-sm font-medium">E-Mail-Typen vom Wartungsmodus ausnehmen</div>
              <div className="mb-3 text-xs text-muted-foreground">
                Ausgenommene E-Mail-Typen gehen auch bei aktivem Wartungsmodus an die echten
                Empfänger (statt an die Redirect-Adresse). Betrifft nur E-Mails, nicht SMS.
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {emailKinds.map((k) => (
                  <label
                    key={k.id}
                    className={`flex items-center justify-between gap-3 rounded-md border p-2 text-sm ${settings.maintenance?.enabled ? '' : 'opacity-50'}`}
                  >
                    <span>{k.label}</span>
                    <Switch
                      checked={exemptKinds.includes(k.id)}
                      onCheckedChange={(v) => toggleExemptKind(k.id, v)}
                      disabled={!settings.maintenance?.enabled}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <div className="flex justify-end">
            <Button onClick={saveGlobal} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Speichern
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageSquare className="mr-2 h-5 w-5" />
            SMS Provider (global)
          </CardTitle>
          <CardDescription>Aktuell wird SMS77/seven.io unterstützt. Ohne Konfiguration wird SMS-Versand blockiert.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Provider</label>
              <Select
                value={settings.sms.provider}
                onValueChange={(v) => setSettings(prev => ({ ...prev, sms: { ...prev.sms, provider: v as SmsProvider } }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Provider auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Deaktiviert</SelectItem>
                  <SelectItem value="sms77">SMS77 (seven.io)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium">API Key</label>
              <Input
                type="password"
                value={settings.sms.apiKey}
                placeholder="***"
                onChange={(e) => setSettings(prev => ({ ...prev, sms: { ...prev.sms, apiKey: e.target.value } }))}
              />
              <div className="mt-1 text-xs text-muted-foreground">
                Wird nach dem Laden maskiert (***). Wenn unverändert, bleibt der Key im Backend erhalten.
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveGlobal} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Speichern
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Send className="mr-2 h-5 w-5" />
            Test‑SMS
          </CardTitle>
          <CardDescription>Sendet eine Testnachricht mit dem Absender des gewählten Standorts.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Standort</label>
              <Select value={testStandortId} onValueChange={setTestStandortId}>
                <SelectTrigger>
                  <SelectValue placeholder="Standort auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {standorte.map(s => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Empfänger (to)</label>
              <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+49176123456789" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Text</label>
              <Input value={testText} onChange={(e) => setTestText(e.target.value)} placeholder="SIDIGO Test-SMS" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={sendTestSms} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Senden...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Test senden
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <PhoneCall className="mr-2 h-5 w-5" />
            Click‑to‑Dial / CTI (global)
          </CardTitle>
          <CardDescription>Steuert das Verhalten beim Klick auf „Anrufen“ (z. B. NFON AKZ‑Prefix in Clipboard).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Provider</label>
              <Select
                value={settings.cti.provider}
                onValueChange={(v) => setSettings(prev => ({ ...prev, cti: { provider: v as CtiProvider } }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Provider auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Standard (tel:)</SelectItem>
                  <SelectItem value="nfon">NFON (AKZ Prefix)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-end justify-end">
              <Button onClick={saveGlobal} disabled={loading}>
                {loading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Speichern
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Standort‑Konfiguration</CardTitle>
          <CardDescription>SMS‑Absender (from), E‑Mail Absender und NFON AKZ pro Standort festlegen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {standorte.length === 0 ? (
            <div className="text-sm text-muted-foreground">Keine Standorte gefunden.</div>
          ) : (
            standorte.map((s) => {
              const cfg = standortMap[s.id] || { smsSender: '', nfonAkz: '', emailFrom: '', emailFromName: '' };
              return (
                <div key={s.id} className="rounded-md border p-4 space-y-3">
                  <div className="text-base font-semibold">{s.name}</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium">SMS Absender (from)</label>
                      <Input
                        value={cfg.smsSender}
                        onChange={(e) => updateStandortField(s.id, { smsSender: e.target.value })}
                        placeholder="z.B. SIDIGO"
                        aria-invalid={smsAbsenderFehler(cfg.smsSender) !== '' || undefined}
                      />
                      {smsAbsenderFehler(cfg.smsSender) ? (
                        <div className="mt-1 text-xs text-destructive">
                          {smsAbsenderFehler(cfg.smsSender)}
                          {smsAbsenderVorschlag(cfg.smsSender) ? (
                            <>
                              {' '}
                              <button
                                type="button"
                                className="underline underline-offset-2"
                                onClick={() =>
                                  updateStandortField(s.id, {
                                    smsSender: smsAbsenderVorschlag(cfg.smsSender),
                                  })
                                }
                              >
                                „{smsAbsenderVorschlag(cfg.smsSender)}" übernehmen
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : (
                        <div className="mt-1 text-xs text-muted-foreground">
                          max. 11 Zeichen (Buchstaben, Ziffern, Leerzeichen) oder eine Rufnummer
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">NFON AKZ</label>
                      <Input
                        value={cfg.nfonAkz}
                        onChange={(e) => updateStandortField(s.id, { nfonAkz: e.target.value })}
                        placeholder="z.B. 1"
                      />
                      <div className="mt-1 text-xs text-muted-foreground">wird als #{`AKZ`} vor die Nummer gesetzt</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium">E‑Mail Absenderadresse (from)</label>
                      <Input
                        value={cfg.emailFrom}
                        onChange={(e) => updateStandortField(s.id, { emailFrom: e.target.value })}
                        placeholder="z.B. berlin@fitinmusic.com"
                      />
                      <div className="mt-1 text-xs text-muted-foreground">Wird für SMTP‑Versand aus Office‑UI genutzt (z.B. Probestunden‑Anfragen).</div>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Absendername (optional)</label>
                      <Input
                        value={cfg.emailFromName}
                        onChange={(e) => updateStandortField(s.id, { emailFromName: e.target.value })}
                        placeholder="z.B. FitInMusic Berlin"
                      />
                    </div>
                  </div>

                  <div className="flex items-end justify-end">
                    <Button onClick={() => saveStandort(s.id)} disabled={savingStandortId === s.id}>
                      {savingStandortId === s.id ? (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          Speichern...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Standort speichern
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}


